export const WEEK = 'WEEK';
export const MONTH = 'MONTH';
export const QUARTER = 'QUARTER';
export const YEAR = 'YEAR';

export const PERIOD_SCOPE = {
  CURRENT: 'Current',
  PREVIOUS: 'Previous',
};

const getMonthNumber = monthName => {
  const monthMap = {
    january: '1',
    february: '2',
    march: '3',
    april: '4',
    may: '5',
    june: '6',
    july: '7',
    august: '8',
    september: '9',
    october: '10',
    november: '11',
    december: '12',
    jan: '1',
    feb: '2',
    mar: '3',
    apr: '4',
    jun: '6',
    jul: '7',
    aug: '8',
    sep: '9',
    oct: '10',
    nov: '11',
    dec: '12',
  };

  const lowerMonthName = monthName.toLowerCase();

  return monthMap[lowerMonthName] ?? monthName;
};

/**
 * Takes a date string in the format "YYYY-MM-DD", "YYYY-M-D", "YYYY-M", "YYYY" and returns a Date object at Noon UTC.
 * "YYYY-M" defaults to the 1st of the month. "YYYY" defaults to January 1.
 *
 * Month can also be a month name or a 3 character month abbreviation.
 *
 * The returned Date should guarantee that the actual date does not change regardless of the browser's timezone.
 */
export const utcDate = date => {
  if (date == null) {
    return undefined;
  }

  const dateParts = date.split('-');

  if (dateParts.length >= 2) {
    dateParts[1] = getMonthNumber(dateParts[1]);
  }

  let dateString = dateParts.map(part => part.padStart(2, '0')).join('-');

  if (dateParts.length === 1) {
    dateString = dateString.concat('-01-01');
  } else if (dateParts.length === 2) {
    dateString = dateString.concat('-01');
  }

  return new Date(`${dateString}T12:00:00Z`);
};

/**
 * This is an example extract of the meta from usePerformanceForecastMeta().
 * Note that each is "relative" to "as_of_date"
 * 
  "as_of_date": "2025-03-25",
  "relative_dates": {
    "yesterday": "2025-03-24",
    "next_7_start": "2025-03-25",
    "next_7_end": "2025-03-31",
    "next_30_start": "2025-03-25",
    "next_30_end": "2025-04-23",
    "next_90_start": "2025-03-25",
    "next_90_end": "2025-06-22",
    "next_365_start": "2025-03-25",
    "next_365_end": "2026-03-24",
    "week_start": "2025-03-24",
    "week_end": "2025-03-30",
    "week_last_start": "2025-03-17",
    "week_last_end": "2025-03-23",
    "week_last_4_start": "2025-02-25",
    "week_last_4_end": "2025-03-24",
    "week_prior_start": "2025-03-10",
    "week_prior_end": "2025-03-16",
    "month_start": "2025-03-01",
    "month_end": "2025-03-31",
    "month_last_start": "2025-02-01",
    "month_last_end": "2025-02-28",
    "month_prior_start": "2025-01-01",
    "month_prior_end": "2025-01-31",
    "month_next_start": "2025-04-01",
    "month_next_end": "2025-04-30",
    "quarter_start": "2025-01-01",
    "quarter_end": "2025-03-31",
    "quarter_last_start": "2024-10-01",
    "quarter_last_end": "2024-12-31",
    "quarter_prior_start": "2024-07-01",
    "quarter_prior_end": "2024-09-30",
    "quarter_next_start": "2025-04-01",
    "quarter_next_end": "2025-06-30",
    "year_start": "2025-01-01",
    "year_end": "2025-12-31",
    "year_next_start": "2026-01-01",
    "year_next_end": "2026-12-31",
    "sdlw_yesterday": "2025-03-17",
    "sdly_yesterday": "2024-03-25",
    "stly_next_7_start": "2024-03-25",
    "stly_next_7_end": "2024-03-31",
    "stly_next_30_start": "2024-03-25",
    "stly_next_30_end": "2024-04-23",
    "stly_next_90_start": "2024-03-25",
    "stly_next_90_end": "2024-06-22",
    "stly_next_365_start": "2024-03-25",
    "stly_next_365_end": "2025-03-24",
    "stly_week_start": "2024-03-25",
    "stly_week_end": "2024-03-31",
    "stly_week_last_start": "2024-03-18",
    "stly_week_last_end": "2024-03-24",
    "stly_week_last_4_start": "2024-02-27",
    "stly_week_last_4_end": "2024-03-25",
    "stly_week_prior_start": "2024-03-11",
    "stly_week_prior_end": "2024-03-17",
    "stly_month_start": "2024-03-01",
    "stly_month_end": "2024-03-31",
    "stly_month_last_start": "2024-02-01",
    "stly_month_last_end": "2024-02-29",
    "stly_month_prior_start": "2024-01-01",
    "stly_month_prior_end": "2024-01-31",
    "stly_month_next_start": "2024-04-01",
    "stly_month_next_end": "2024-04-30",
    "stly_month_to_date_start": "2024-03-01",
    "stly_month_to_date_end": "2024-03-25",
    "stly_quarter_start": "2024-01-01",
    "stly_quarter_end": "2024-03-31",
    "stly_quarter_last_start": "2023-10-01",
    "stly_quarter_last_end": "2023-12-31",
    "stly_quarter_next_start": "2024-04-01",
    "stly_quarter_next_end": "2024-06-30",
    "stly_quarter_to_date_start": "2024-01-01",
    "stly_quarter_to_date_end": "2024-03-25",
    "stly_year_start": "2024-01-01",
    "stly_year_end": "2024-12-31",
    "stly_year_prior_start": "2023-01-01",
    "stly_year_prior_end": "2023-12-31",
    "stly_year_to_date_start": "2024-01-01",
    "stly_year_to_date_end": "2024-03-25"
  }
 */

/**
 * Converts each relative date string into a Date. Note that not every possible entry is converted.
 * Simply add missing dates as needed.
 *
 * @param {Object} relativeDates - the relative_dates object from usePerformanceForecastMeta()
 * @returns {Object} An object whose attributes are Date objects for each relative date
 */
export const parseRelativeDates = relativeDates => {
  if (relativeDates === undefined) {
    return {};
  }

  const {
    yesterday,

    week_start: weekStart,
    week_end: weekEnd,
    week_last_start: weekLastStart,
    week_last_end: weekLastEnd,
    week_prior_start: weekPriorStart,
    week_prior_end: weekPriorEnd,
    week_next_start: weekNextStart,
    week_next_end: weekNextEnd,
    stly_week_start: stlyWeekStart,
    stly_week_end: stlyWeekEnd,
    stly_week_last_start: stlyWeekLastStart,
    stly_week_last_end: stlyWeekLastEnd,
    stly_week_next_start: stlyWeekNextStart,
    stly_week_next_end: stlyWeekNextEnd,

    month_start: monthStart,
    month_end: monthEnd,
    month_last_start: monthLastStart,
    month_last_end: monthLastEnd,
    month_prior_start: monthPriorStart,
    month_prior_end: monthPriorEnd,
    month_next_start: monthNextStart,
    month_next_end: monthNextEnd,
    stly_month_start: stlyMonthStart,
    stly_month_end: stlyMonthEnd,
    stly_month_last_start: stlyMonthLastStart,
    stly_month_last_end: stlyMonthLastEnd,
    stly_month_next_start: stlyMonthNextStart,
    stly_month_next_end: stlyMonthNextEnd,

    quarter_start: quarterStart,
    quarter_end: quarterEnd,
    quarter_last_start: quarterLastStart,
    quarter_last_end: quarterLastEnd,
    quarter_prior_start: quarterPriorStart,
    quarter_prior_end: quarterPriorEnd,
    quarter_next_start: quarterNextStart,
    quarter_next_end: quarterNextEnd,
    stly_quarter_start: stlyQuarterStart,
    stly_quarter_end: stlyQuarterEnd,
    stly_quarter_last_start: stlyQuarterLastStart,
    stly_quarter_last_end: stlyQuarterLastEnd,
    stly_quarter_next_start: stlyQuarterNextStart,
    stly_quarter_next_end: stlyQuarterNextEnd,

    year_start: yearStart,
    year_end: yearEnd,
    year_next_start: yearNextStart,
    year_next_end: yearNextEnd,
    stly_year_start: stlyYearStart,
    stly_year_end: stlyYearEnd,
    stly_year_prior_start: stlyYearPriorStart,
    stly_year_prior_end: stlyYearPriorEnd,
  } = relativeDates;

  return {
    yesterday: utcDate(yesterday),

    weekStart: utcDate(weekStart),
    weekEnd: utcDate(weekEnd),
    weekLastStart: utcDate(weekLastStart),
    weekLastEnd: utcDate(weekLastEnd),
    weekPriorStart: utcDate(weekPriorStart),
    weekPriorEnd: utcDate(weekPriorEnd),
    weekNextStart: utcDate(weekNextStart),
    weekNextEnd: utcDate(weekNextEnd),
    stlyWeekStart: utcDate(stlyWeekStart),
    stlyWeekEnd: utcDate(stlyWeekEnd),
    stlyWeekLastStart: utcDate(stlyWeekLastStart),
    stlyWeekLastEnd: utcDate(stlyWeekLastEnd),
    stlyWeekNextStart: utcDate(stlyWeekNextStart),
    stlyWeekNextEnd: utcDate(stlyWeekNextEnd),

    monthStart: utcDate(monthStart),
    monthEnd: utcDate(monthEnd),
    monthLastStart: utcDate(monthLastStart),
    monthLastEnd: utcDate(monthLastEnd),
    monthPriorStart: utcDate(monthPriorStart),
    monthPriorEnd: utcDate(monthPriorEnd),
    monthNextStart: utcDate(monthNextStart),
    monthNextEnd: utcDate(monthNextEnd),
    stlyMonthStart: utcDate(stlyMonthStart),
    stlyMonthEnd: utcDate(stlyMonthEnd),
    stlyMonthLastStart: utcDate(stlyMonthLastStart),
    stlyMonthLastEnd: utcDate(stlyMonthLastEnd),
    stlyMonthNextStart: utcDate(stlyMonthNextStart),
    stlyMonthNextEnd: utcDate(stlyMonthNextEnd),

    quarterStart: utcDate(quarterStart),
    quarterEnd: utcDate(quarterEnd),
    quarterLastStart: utcDate(quarterLastStart),
    quarterLastEnd: utcDate(quarterLastEnd),
    quarterPriorStart: utcDate(quarterPriorStart),
    quarterPriorEnd: utcDate(quarterPriorEnd),
    quarterNextStart: utcDate(quarterNextStart),
    quarterNextEnd: utcDate(quarterNextEnd),
    stlyQuarterStart: utcDate(stlyQuarterStart),
    stlyQuarterEnd: utcDate(stlyQuarterEnd),
    stlyQuarterLastStart: utcDate(stlyQuarterLastStart),
    stlyQuarterLastEnd: utcDate(stlyQuarterLastEnd),
    stlyQuarterNextStart: utcDate(stlyQuarterNextStart),
    stlyQuarterNextEnd: utcDate(stlyQuarterNextEnd),

    yearStart: utcDate(yearStart),
    yearEnd: utcDate(yearEnd),
    yearNextStart: utcDate(yearNextStart),
    yearNextEnd: utcDate(yearNextEnd),
    stlyYearStart: utcDate(stlyYearStart),
    stlyYearEnd: utcDate(stlyYearEnd),
    stlyYearPriorStart: utcDate(stlyYearPriorStart),
    stlyYearPriorEnd: utcDate(stlyYearPriorEnd),
  };
};

const getWeekScopeRanges = (relativeDates, periodScope) => {
  const {
    weekStart,
    weekEnd,
    weekLastStart,
    weekLastEnd,
    weekPriorStart,
    weekPriorEnd,
    stlyWeekStart,
    stlyWeekEnd,
    stlyWeekLastStart,
    stlyWeekLastEnd,
  } = relativeDates;

  let currentRange;
  let lastRange;
  let stlyRange;

  switch (periodScope) {
    case PERIOD_SCOPE.CURRENT: {
      currentRange = { start: weekStart, end: weekEnd };
      lastRange = { start: weekLastStart, end: weekLastEnd };
      stlyRange = { start: stlyWeekStart, end: stlyWeekEnd };
      break;
    }
    case PERIOD_SCOPE.PREVIOUS: {
      currentRange = { start: weekLastStart, end: weekLastEnd };
      lastRange = { start: weekPriorStart, end: weekPriorEnd };
      stlyRange = { start: stlyWeekLastStart, end: stlyWeekLastEnd };
      break;
    }
    default:
      break;
  }

  return { currentRange, lastRange, stlyRange };
};

const getMonthScopeRanges = (relativeDates, periodScope) => {
  const {
    monthStart,
    monthEnd,
    monthLastStart,
    monthLastEnd,
    monthPriorStart,
    monthPriorEnd,
    stlyMonthStart,
    stlyMonthEnd,
    stlyMonthLastStart,
    stlyMonthLastEnd,
  } = relativeDates;

  let currentRange;
  let lastRange;
  let stlyRange;

  switch (periodScope) {
    case PERIOD_SCOPE.CURRENT: {
      currentRange = { start: monthStart, end: monthEnd };
      lastRange = { start: monthLastStart, end: monthLastEnd };
      stlyRange = { start: stlyMonthStart, end: stlyMonthEnd };
      break;
    }
    case PERIOD_SCOPE.PREVIOUS: {
      currentRange = { start: monthLastStart, end: monthLastEnd };
      lastRange = { start: monthPriorStart, end: monthPriorEnd };
      stlyRange = { start: stlyMonthLastStart, end: stlyMonthLastEnd };
      break;
    }
    default:
      break;
  }

  return { currentRange, lastRange, stlyRange };
};

const getQuarterScopeRanges = (relativeDates, periodScope) => {
  const {
    quarterStart,
    quarterEnd,
    quarterLastStart,
    quarterLastEnd,
    quarterPriorStart,
    quarterPriorEnd,
    stlyQuarterStart,
    stlyQuarterEnd,
    stlyQuarterLastStart,
    stlyQuarterLastEnd,
  } = relativeDates;

  let currentRange;
  let lastRange;
  let stlyRange;

  switch (periodScope) {
    case PERIOD_SCOPE.CURRENT: {
      currentRange = { start: quarterStart, end: quarterEnd };
      lastRange = { start: quarterLastStart, end: quarterLastEnd };
      stlyRange = { start: stlyQuarterStart, end: stlyQuarterEnd };
      break;
    }
    case PERIOD_SCOPE.PREVIOUS: {
      currentRange = { start: quarterLastStart, end: quarterLastEnd };
      lastRange = { start: quarterPriorStart, end: quarterPriorEnd };
      stlyRange = { start: stlyQuarterLastStart, end: stlyQuarterLastEnd };
      break;
    }
    default:
      break;
  }

  return { currentRange, lastRange, stlyRange };
};

const getYearScopeRanges = (relativeDates, periodScope) => {
  const { yearStart, yearEnd, stlyYearStart, stlyYearEnd, stlyYearPriorStart, stlyYearPriorEnd } = relativeDates;

  let currentRange;
  let lastRange;
  let stlyRange;

  switch (periodScope) {
    case PERIOD_SCOPE.CURRENT: {
      currentRange = { start: yearStart, end: yearEnd };
      lastRange = { start: stlyYearStart, end: stlyYearEnd };
      stlyRange = null;
      break;
    }
    case PERIOD_SCOPE.PREVIOUS: {
      currentRange = { start: stlyYearStart, end: stlyYearEnd };
      lastRange = { start: stlyYearPriorStart, end: stlyYearPriorEnd };
      stlyRange = null;
      break;
    }
    default:
      break;
  }

  return { currentRange, lastRange, stlyRange };
};

export const getRanges = (period, relativeDates, periodScope) => {
  const granularity = period === WEEK || period === MONTH ? 'day' : 'month';

  const scopeGetters = {
    [WEEK]: getWeekScopeRanges,
    [MONTH]: getMonthScopeRanges,
    [QUARTER]: getQuarterScopeRanges,
    [YEAR]: getYearScopeRanges,
  };

  const scopeFn = scopeGetters[period];
  if (!scopeFn) {
    return { granularity, currentRange: null, lastRange: null, stlyRange: null };
  }

  const { currentRange, lastRange, stlyRange } = scopeFn(relativeDates, periodScope);

  return { granularity, currentRange, lastRange, stlyRange, periodScope };
};

const DAY_MS = 24 * 60 * 60 * 1000;
const clamp = (n, min, max) => Math.max(min, Math.min(max, n));
const addDaysUTC = (d, days) =>
  new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + days, 12, 0, 0));
const ymIndex = d => d.getUTCFullYear() * 12 + d.getUTCMonth();
const diffInDays = (a, b) => Math.round((a - b) / DAY_MS);
const diffInMonths = (a, b) => ymIndex(a) - ymIndex(b);

const toYMD = date => `${date.getUTCFullYear()}-${date.getUTCMonth() + 1}-${date.getUTCDate()}`;

/**
 * Returns the date in the `toRange` aligned to the same relative position
 * that `selectedDate` has within `fromRange`, respecting the granularity.
 */
export const alignedDateInRange = (selectedDate, fromRange, toRange, granularity) => {
  if (!fromRange || !toRange || !selectedDate) {
    return undefined;
  }

  if (granularity === 'day') {
    const fromLen = clamp(diffInDays(fromRange.end, fromRange.start), 0, 10_000);
    const offset = clamp(diffInDays(selectedDate, fromRange.start), 0, fromLen);
    const target = addDaysUTC(toRange.start, offset);

    // clamp to the end of the target range in case it overflows
    const capped = target > toRange.end ? toRange.end : target;

    return utcDate(toYMD(capped));
  }

  // granularity === 'month'
  const fromLen = clamp(diffInMonths(fromRange.end, fromRange.start), 0, 10_000);
  const offset = clamp(diffInMonths(selectedDate, fromRange.start), 0, fromLen);
  const startIdx = ymIndex(toRange.start);
  const endIdx = ymIndex(toRange.end);
  const idx = clamp(startIdx + offset, startIdx, endIdx);
  const year = Math.floor(idx / 12);
  const month = idx % 12;

  return utcDate(`${year}-${month + 1}`);
};
